/* eslint-disable no-await-in-loop, no-restricted-syntax */

import {
  getGroup,
  getPermissionTags,
  putGroup,
  useCreatePermissionTag as baseUseCreatePermissionTag,
} from '@vlabs/api/artemy/client';
import type {
  GroupRecord,
} from '@vlabs/api/artemy/model';
import type { PermissionTag } from '@vlabs/api/artemy/model/PermissionTag';
import { useHasPermission } from '@vlabs/permissions';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import type { CreatePermissionTagProps } from '../types';

// проверяет, существует ли уже тег с таким названием
async function checkPermissionTagExists(permissionTag: PermissionTag): Promise<void> {
  const existingPermissionTags = await getPermissionTags({ permission_tags: permissionTag });

  if (existingPermissionTags.permission_tags?.some((tag) => tag.permission_tag === permissionTag)) {
    throw new Error('permission_tag_exists');
  }
}

// последовательно привязывает выбранные группы к новому тегу
async function updateGroups(
  groups: GroupRecord[],
  permissionTag: PermissionTag,
  onGroupUpdateError: (error: unknown, group: GroupRecord) => void,
): Promise<boolean> {
  let hasError = false;

  for (const group of groups) {
    try {
      const groupDetails = await getGroup(group.group_id);

      await putGroup(groupDetails.group_id, {
        analytics: groupDetails.analytics,
        description: groupDetails.description,
        group_name: groupDetails.group_name,
        owner_department: groupDetails.owner_department,
        stream_settings: groupDetails.stream_settings,
        permission_tag: permissionTag,
      });
    } catch (error) {
      hasError = true;
      onGroupUpdateError(error, group);
    }
  }

  return hasError;
}

interface CreatePermissionTagFormSubmitValues {
  groups: GroupRecord[];
  permissionTag: PermissionTag;
}

interface UseCreatePermissionTagProps extends CreatePermissionTagProps {
  onSuccess?: VoidFunction;
}

interface UseCreatePermissionTagReturn {
  onSubmit: (values: CreatePermissionTagFormSubmitValues) => Promise<'success' | 'duplicate' | 'error'>;
  isPending: boolean;
}

export function useCreatePermissionTag({
  onCreatePermissionTag = undefined,
  onSuccess = undefined,
}: UseCreatePermissionTagProps = {}): UseCreatePermissionTagReturn {
  const { t } = useTranslation();
  const canUpdateGroups = useHasPermission('group', 'update');
  const createPermissionTag = baseUseCreatePermissionTag();

  async function createTag(permissionTag: PermissionTag): Promise<PermissionTag> {
    await checkPermissionTagExists(permissionTag);

    const { permission_tag: createdPermissionTag } = await createPermissionTag.mutateAsync({
      data: {
        permission_tag: permissionTag,
      },
    });

    return createdPermissionTag;
  }

  function showGroupUpdateError(error: unknown, group: GroupRecord): void {
    const responseStatus = typeof error === 'object' && error && 'response' in error
      ? (error as { response?: { status?: number } }).response?.status
      : undefined;

    if (responseStatus === 403 && !canUpdateGroups) {
      toast.error(t('features:create_permission_tag.create_permission_tag_form.validation.permissions_error'));

      return;
    }

    if (responseStatus === 403) {
      toast.error(t('features:create_permission_tag.create_permission_tag_form.validation.group_access_error', {
        groupName: group.group_name,
      }));

      return;
    }

    toast.error(t('features:create_permission_tag.on_groups_update_error'));
  }

  async function onSubmit({
    groups,
    permissionTag,
  }: CreatePermissionTagFormSubmitValues): Promise<'success' | 'duplicate' | 'error'> {
    let createdPermissionTag: PermissionTag;

    try {
      createdPermissionTag = await createTag(permissionTag);
    } catch (error) {
      const responseStatus = typeof error === 'object' && error && 'response' in error
        ? (error as { response?: { status?: number } }).response?.status
        : undefined;

      if ((error instanceof Error && error.message === 'permission_tag_exists') || responseStatus === 409) {
        return 'duplicate';
      }

      toast.error(t('features:create_permission_tag.on_error'));

      return 'error';
    }

    const hasGroupsUpdateError = await updateGroups(groups, permissionTag, showGroupUpdateError);

    onSuccess?.();
    onCreatePermissionTag?.(createdPermissionTag);

    if (hasGroupsUpdateError) {
      return 'success';
    }

    toast.success(t('features:create_permission_tag.on_success'));

    return 'success';
  }

  return {
    onSubmit,
    isPending: createPermissionTag.isPending,
  };
}
